import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { getInstaller, getInstallerProjects, updateInstaller, updateInstallerStatus } from './api';
function statusLabel(status) {
    switch (status) {
        case 'draft': return 'Draft';
        case 'assigned': return 'Assigned';
        case 'in_progress': return 'In Progress';
        case 'completed': return 'Completed';
        default: return status;
    }
}
function projectStatusStyle(status) {
    switch (status) {
        case 'draft': return styles.badgeGray;
        case 'assigned': return styles.badgeBlue;
        case 'in_progress': return styles.badgeOrange;
        case 'completed': return styles.badgeGreen;
        default: return styles.badgeGray;
    }
}
export const InstallerDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [installer, setInstaller] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [draft, setDraft] = useState({});
    const [newSkill, setNewSkill] = useState('');
    const [projects, setProjects] = useState([]);
    const [projectsLoading, setProjectsLoading] = useState(false);
    const loadInstaller = useCallback(async () => {
        if (!id)
            return;
        setLoading(true);
        setError(null);
        try {
            const data = await getInstaller(id);
            setInstaller(data);
        }
        catch (e) {
            setError(e.message ?? 'Failed to load installer');
        }
        finally {
            setLoading(false);
        }
    }, [id]);
    const loadProjects = useCallback(async () => {
        if (!id)
            return;
        setProjectsLoading(true);
        try {
            const data = await getInstallerProjects(id, { limit: 50 });
            const items = Array.isArray(data) ? data : (data?.items ?? []);
            setProjects(items);
        }
        catch {
            // non-critical
        }
        finally {
            setProjectsLoading(false);
        }
    }, [id]);
    useEffect(() => {
        loadInstaller();
        loadProjects();
    }, [loadInstaller, loadProjects]);
    const startEdit = () => {
        if (!installer)
            return;
        setDraft({
            firstName: installer.firstName,
            lastName: installer.lastName,
            email: installer.email,
            phone: installer.phone,
            skills: [...installer.skills],
        });
        setSaveError(null);
        setEditMode(true);
    };
    const cancelEdit = () => {
        setEditMode(false);
        setDraft({});
        setSaveError(null);
        setNewSkill('');
    };
    const handleSave = async () => {
        if (!id || !installer)
            return;
        setSaving(true);
        setSaveError(null);
        try {
            const updated = await updateInstaller(id, draft);
            setInstaller(updated);
            setEditMode(false);
            setDraft({});
        }
        catch (e) {
            setSaveError(e.message ?? 'Failed to save');
        }
        finally {
            setSaving(false);
        }
    };
    const handleToggleStatus = async () => {
        if (!id || !installer)
            return;
        const newStatus = installer.status === 'active' ? 'inactive' : 'active';
        try {
            const updated = await updateInstallerStatus(id, newStatus);
            setInstaller(updated);
        }
        catch (e) {
            setError(e.message ?? 'Failed to update status');
        }
    };
    const addSkill = () => {
        const trimmed = newSkill.trim();
        if (!trimmed)
            return;
        const skills = draft.skills ?? [];
        if (!skills.includes(trimmed)) {
            setDraft((p) => ({ ...p, skills: [...skills, trimmed] }));
        }
        setNewSkill('');
    };
    const removeSkill = (skill) => {
        setDraft((p) => ({ ...p, skills: (p.skills ?? []).filter((s) => s !== skill) }));
    };
    if (loading) {
        return (_jsx(Layout, { children: _jsx("div", { style: styles.loadingCenter, children: _jsx("div", { style: styles.spinner }) }) }));
    }
    if (error && !installer) {
        return (_jsxs(Layout, { children: [_jsx("div", { style: styles.errorBanner, children: error }), _jsx("button", { style: styles.backLink, onClick: () => navigate('/installers'), children: "\u2190 Back to Team" })] }));
    }
    if (!installer)
        return null;
    return (_jsx(Layout, { children: _jsxs("div", { children: [_jsx("button", { style: styles.backLink, onClick: () => navigate('/installers'), children: "\u2190 Team" }), _jsxs("div", { style: styles.card, children: [_jsxs("div", { style: styles.cardHeaderRow, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }, children: [_jsxs("h2", { style: styles.installerName, children: [installer.firstName, " ", installer.lastName] }), _jsx("span", { style: installer.status === 'active' ? styles.badgeGreen : styles.badgeGray, children: installer.status === 'active' ? 'Active' : 'Inactive' })] }), _jsxs("div", { style: { display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }, children: [!editMode && (_jsx("button", { style: installer.status === 'active' ? styles.dangerButton : styles.successButton, onClick: handleToggleStatus, children: installer.status === 'active' ? 'Deactivate' : 'Activate' })), !editMode ? (_jsx("button", { style: styles.secondaryButton, onClick: startEdit, children: "Edit" })) : (_jsxs(_Fragment, { children: [_jsx("button", { style: styles.ghostButton, onClick: cancelEdit, disabled: saving, children: "Cancel" }), _jsx("button", { style: styles.primaryButton, onClick: handleSave, disabled: saving, children: saving ? 'Saving…' : 'Save' })] }))] })] }), saveError && _jsx("div", { style: { ...styles.errorBanner, marginTop: '12px' }, children: saveError }), error && _jsx("div", { style: { ...styles.errorBanner, marginTop: '12px' }, children: error })] }), _jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.sectionTitle, children: "Profile" }), _jsxs("div", { style: styles.fieldGrid, children: [_jsx(ProfileField, { label: "First Name", editing: editMode, value: editMode ? (draft.firstName ?? '') : installer.firstName, onChange: (v) => setDraft((p) => ({ ...p, firstName: v })) }), _jsx(ProfileField, { label: "Last Name", editing: editMode, value: editMode ? (draft.lastName ?? '') : installer.lastName, onChange: (v) => setDraft((p) => ({ ...p, lastName: v })) }), _jsx(ProfileField, { label: "Email", editing: editMode, value: editMode ? (draft.email ?? '') : installer.email, onChange: (v) => setDraft((p) => ({ ...p, email: v })), type: "email" }), _jsx(ProfileField, { label: "Phone", editing: editMode, value: editMode ? (draft.phone ?? '') : (installer.phone || '—'), onChange: (v) => setDraft((p) => ({ ...p, phone: v })), type: "tel" })] })] }), _jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.sectionTitle, children: "Skills" }), editMode ? (_jsxs("div", { children: [_jsx("div", { style: styles.skillsRow, children: (draft.skills ?? []).map((skill) => (_jsxs("span", { style: styles.skillChipEdit, children: [skill, _jsx("button", { style: styles.skillRemoveBtn, onClick: () => removeSkill(skill), "aria-label": `Remove ${skill}`, children: "\u00D7" })] }, skill))) }), _jsxs("div", { style: { display: 'flex', gap: '8px', marginTop: '12px' }, children: [_jsx("input", { style: { ...styles.input, maxWidth: '240px' }, type: "text", placeholder: "Add a skill\u2026", value: newSkill, onChange: (e) => setNewSkill(e.target.value), onKeyDown: (e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    addSkill();
                                                }
                                            } }), _jsx("button", { style: styles.addItemButton, onClick: addSkill, children: "Add" })] })] })) : installer.skills.length === 0 ? (_jsx("p", { style: styles.emptySubText, children: "No skills listed." })) : (_jsx("div", { style: styles.skillsRow, children: installer.skills.map((skill) => (_jsx("span", { style: styles.skillChip, children: skill }, skill))) }))] }), _jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.sectionTitle, children: "Assigned Projects" }), projectsLoading ? (_jsx("div", { style: styles.loadingCenter, children: _jsx("div", { style: styles.spinner }) })) : projects.length === 0 ? (_jsx("p", { style: styles.emptySubText, children: "No projects assigned." })) : (_jsx("div", { children: projects.map((project, idx) => (_jsxs("div", { style: styles.projectRow, children: [_jsx("span", { style: styles.projectTitle, children: project.title ?? '—' }), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '10px' }, children: [_jsx("span", { style: projectStatusStyle(project.status), children: statusLabel(project.status) }), project.scheduledDate && (_jsx("span", { style: { fontSize: '13px', color: '#86868b' }, children: new Date(project.scheduledDate).toLocaleDateString() }))] })] }, project.id ?? idx))) }))] })] }) }));
};
const ProfileField = ({ label, editing, value, onChange, type = 'text', }) => (_jsxs("div", { style: styles.fieldGroup, children: [_jsx("label", { style: styles.fieldLabel, children: label }), editing ? (_jsx("input", { style: styles.input, type: type, value: value, onChange: (e) => onChange(e.target.value) })) : (_jsx("p", { style: styles.fieldValue, children: value || '—' }))] }));
const styles = {
    loadingCenter: {
        display: 'flex',
        justifyContent: 'center',
        padding: '80px 0',
    },
    spinner: {
        width: '32px',
        height: '32px',
        border: '3px solid rgba(0,0,0,0.1)',
        borderTopColor: '#0071e3',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    backLink: {
        background: 'none',
        border: 'none',
        color: '#0071e3',
        fontSize: '15px',
        fontWeight: '500',
        cursor: 'pointer',
        padding: '0 0 20px 0',
        display: 'block',
    },
    card: {
        padding: '28px 32px',
        backgroundColor: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        marginBottom: '20px',
    },
    cardHeaderRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px',
    },
    installerName: {
        fontSize: '32px',
        fontWeight: '700',
        margin: 0,
        color: '#1d1d1f',
        letterSpacing: '-0.5px',
    },
    sectionTitle: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#1d1d1f',
        margin: '0 0 20px 0',
        letterSpacing: '-0.3px',
    },
    fieldGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '16px 24px',
    },
    fieldGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
    },
    fieldLabel: {
        fontSize: '12px',
        fontWeight: '600',
        color: '#86868b',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    fieldValue: {
        margin: 0,
        fontSize: '15px',
        color: '#1d1d1f',
    },
    input: {
        flex: 1,
        padding: '9px 12px',
        fontSize: '15px',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        color: '#1d1d1f',
        outline: 'none',
        minWidth: 0,
        width: '100%',
        boxSizing: 'border-box',
    },
    skillsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
    },
    skillChip: {
        padding: '4px 12px',
        backgroundColor: 'rgba(0,122,255,0.08)',
        color: '#0071e3',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
    },
    skillChipEdit: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 10px 4px 12px',
        backgroundColor: 'rgba(0,122,255,0.08)',
        color: '#0071e3',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
    },
    skillRemoveBtn: {
        background: 'none',
        border: 'none',
        color: '#0071e3',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: '700',
        lineHeight: '1',
        padding: '0',
        display: 'flex',
        alignItems: 'center',
    },
    addItemButton: {
        padding: '9px 18px',
        backgroundColor: 'transparent',
        color: '#0071e3',
        border: '1px solid #0071e3',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    projectRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        backgroundColor: 'rgba(0,0,0,0.025)',
        borderRadius: '10px',
        marginBottom: '8px',
        flexWrap: 'wrap',
        gap: '8px',
    },
    projectTitle: {
        fontSize: '15px',
        fontWeight: '600',
        color: '#1d1d1f',
    },
    primaryButton: {
        padding: '10px 22px',
        backgroundColor: '#0071e3',
        color: '#fff',
        border: 'none',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    secondaryButton: {
        padding: '10px 22px',
        backgroundColor: 'rgba(0,0,0,0.06)',
        color: '#1d1d1f',
        border: 'none',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    ghostButton: {
        padding: '10px 22px',
        backgroundColor: 'transparent',
        color: '#86868b',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer',
    },
    dangerButton: {
        padding: '9px 18px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        color: '#c0392b',
        border: 'none',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    successButton: {
        padding: '9px 18px',
        backgroundColor: 'rgba(52,199,89,0.12)',
        color: '#248a3d',
        border: 'none',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    errorBanner: {
        padding: '12px 16px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        border: '1px solid rgba(255,59,48,0.3)',
        borderRadius: '10px',
        color: '#c0392b',
        fontSize: '14px',
    },
    emptySubText: {
        color: '#86868b',
        fontSize: '14px',
        margin: 0,
    },
    badgeGreen: {
        display: 'inline-block',
        padding: '4px 12px',
        backgroundColor: 'rgba(52,199,89,0.12)',
        color: '#248a3d',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
    },
    badgeGray: {
        display: 'inline-block',
        padding: '4px 12px',
        backgroundColor: 'rgba(142,142,147,0.15)',
        color: '#86868b',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
    },
    badgeBlue: {
        display: 'inline-block',
        padding: '3px 10px',
        backgroundColor: 'rgba(0,122,255,0.12)',
        color: '#0071e3',
        borderRadius: '980px',
        fontSize: '12px',
        fontWeight: '600',
    },
    badgeOrange: {
        display: 'inline-block',
        padding: '3px 10px',
        backgroundColor: 'rgba(255,149,0,0.15)',
        color: '#c45c00',
        borderRadius: '980px',
        fontSize: '12px',
        fontWeight: '600',
    },
};
//# sourceMappingURL=InstallerDetail.js.map